import { cva, cx } from '@vizit/theme';
import { TableHeader as AriaTableHeader, type TableHeaderProps as AriaTableHeaderProps } from 'react-aria-components';

interface TableHeaderProps<T extends object> extends AriaTableHeaderProps<T> {
  /**
   * Additional CSS classes to apply to the header
   *
   * @optional
   * @type string
   */
  className?: string;
}

const headerClasses = cva({
  base: [
    'sticky top-0 z-10',
    'bg-secondary',
    // Note: Using box-shadow instead of border-b because the border doesn't render
    // consistently with sticky positioning when scrolling. The shadow provides a
    // reliable 1px bottom border that remains visible during scroll.
    'shadow-[0_1px_0_0_theme(borderColor.secondary)]',
  ],
});

/**
 * A table header component that wraps React Aria's TableHeader with custom styling.
 *
 * @param props - The props for the TableHeader component
 * @example
 * <TableHeader>
 *   <Column>Product</Column>
 *   <Column>Live score</Column>
 * </TableHeader>
 */
function TableHeader<T extends object>(props: TableHeaderProps<T>) {
  const { className, ...other } = props;

  return <AriaTableHeader className={cx(headerClasses(), className)} {...other} />;
}

export { TableHeader };
